import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import LeadForm from '@/components/LeadForm';
import SEO from '@/components/SEO';
import { API_URL } from '@/lib/utils';
import { Clock, CheckCircle, ArrowRight, GraduationCap, Users, Award, Download, ExternalLink } from 'lucide-react';

// High-quality training images
const IMAGES = {
  training: "https://images.pexels.com/photos/5452285/pexels-photo-5452285.jpeg?auto=compress&cs=tinysrgb&w=1200",
  education: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=800&fit=crop",
  partnership: `${process.env.REACT_APP_BACKEND_URL}/api/files/9f2372c2-9a9c-4171-b23a-d16c47844e61`
};

// Partner logos
const PARTNER_LOGOS = {
  medhavi: "https://customer-assets.emergentagent.com/job_steward-health-hub/artifacts/nqg9p6c4_MSU_Revised-Logo.png",
  skillIndia: "https://customer-assets.emergentagent.com/job_steward-health-hub/artifacts/3zuvhzxy_Skill-India-L.jpg"
};

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partnershipImage, setPartnershipImage] = useState(IMAGES.partnership);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const [coursesRes, settingsRes] = await Promise.all([
          axios.get(`${API_URL}/courses?active_only=true`),
          axios.get(`${API_URL}/settings`)
        ]);
        setCourses(coursesRes.data);
        if (settingsRes.data.partnership_image_url) {
          setPartnershipImage(settingsRes.data.partnership_image_url);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const highlights = [
    { icon: GraduationCap, title: 'Expert Faculty', desc: 'Learn from industry veterans and certified professionals' },
    { icon: Award, title: 'Certified Programs', desc: 'Industry-recognized certifications to boost your career' },
    { icon: Users, title: 'Placement Support', desc: 'Dedicated placement assistance for all graduates' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="courses-page">
      <SEO 
        title="Healthcare Courses & Training - Medhavi Skill University Partner"
        description="Professional healthcare courses: Hospital Quality Manager, Infection Control, Critical Care Nursing, Medical Coding. Approved Training Partner of Medhavi Skill University & Skill India."
        keywords="healthcare courses India, NABH training, hospital quality manager course, infection control training, critical care nursing, medical coding course, Medhavi Skill University, Skill India"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Healthcare Education</span>
          <h1 className="font-manrope text-4xl md:text-5xl font-bold text-[#1a2a5e] mt-2 mb-6">
            Professional Healthcare Courses
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Advance your healthcare career with our industry-recognized certification programs. 
            Learn from experts and gain practical skills that employers value.
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <div key={index} className="flex items-center gap-4 p-6 rounded-xl bg-slate-50">
                <div className="w-14 h-14 bg-[#1a2a5e] rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1a2a5e]">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIHMT Portal Banner */}
      <section className="py-8 bg-gradient-to-r from-[#1a2a5e] to-[#3b7dd8]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white text-center md:text-left">
              <h3 className="font-manrope text-xl font-bold">Steward Institute of Healthcare Management & Technology</h3>
              <p className="text-blue-100 text-sm mt-1">Visit our dedicated learning portal for detailed course information, enrollment, and brochures</p>
            </div>
            <Button asChild size="lg" className="bg-white text-[#1a2a5e] hover:bg-gray-100 font-semibold whitespace-nowrap" data-testid="sihmt-portal-btn">
              <a href="https://www.sihmt.in" target="_blank" rel="noopener noreferrer">
                Visit SIHMT Portal <ExternalLink className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {courses.map((course) => (
              <Card key={course.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden course-card">
                <CardContent className="p-0">
                  <div className="bg-gradient-to-r from-[#1a2a5e] to-[#3b7dd8] p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-manrope text-xl font-bold">{course.title}</h3>
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {course.duration}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-gray-600 mb-6">{course.description}</p>
                    
                    {course.features && course.features.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-[#1a2a5e] mb-3">What You'll Learn:</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {course.features.map((feature, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="h-4 w-4 text-[#2eb24c] flex-shrink-0" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-4 border-t">
                      {course.price && (
                        <div>
                          <span className="text-sm text-gray-500">Course Fee</span>
                          <p className="font-bold text-[#1a2a5e]">{course.price}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-3 ml-auto">
                        <Button asChild className="bg-[#2eb24c] hover:bg-[#259a40]" data-testid={`enroll-btn-${course.slug}`}>
                          <a href="https://www.sihmt.in" target="_blank" rel="noopener noreferrer">
                            Enroll Now <ExternalLink className="ml-2 h-4 w-4" />
                          </a>
                        </Button>
                        <Button asChild variant="outline" className="border-[#1a2a5e] text-[#1a2a5e] hover:bg-[#1a2a5e] hover:text-white" data-testid={`brochure-btn-${course.slug}`}>
                          <a href={course.brochure_url || "https://www.sihmt.in"} target="_blank" rel="noopener noreferrer">
                            <Download className="mr-2 h-4 w-4" />
                            Brochure
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {courses.length === 0 && (
            <div className="text-center py-12">
              <GraduationCap className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No courses available at the moment.</p>
              <p className="text-gray-400">Check back soon for new programs!</p>
            </div>
          )}
        </div>
      </section>

      {/* Academic Partnership */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          {/* Partner Logos Banner */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
            <p className="text-center text-gray-600 font-medium mb-6">Approved Training Partner of</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
              <img src={PARTNER_LOGOS.medhavi} alt="Medhavi Skill University" className="h-16 md:h-20 object-contain" />
              <img src={PARTNER_LOGOS.skillIndia} alt="Skill India" className="h-14 md:h-16 object-contain" />
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Academic Partnership</span>
              <h2 className="font-manrope text-3xl font-bold text-[#1a2a5e] mt-2 mb-6">
                Approved Training Partner of Medhavi Skill University & Skill India
              </h2>
              <p className="text-gray-600 mb-6">
                We are proud to be an Approved Training Partner of Medhavi Skill University and Skill India. 
                We offer UGC-recognized degree and diploma programs in healthcare management and allied health sciences. 
                Our students benefit from a blend of academic rigor and practical industry experience.
              </p>
              <ul className="space-y-3 mb-8">
                {['UGC Recognized Degrees', 'Skill India Certified', 'Industry-Aligned Curriculum', 'Practical Training', 'Internship Opportunities', 'Placement Support'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="h-5 w-5 text-[#2eb24c]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button asChild className="bg-[#1a2a5e] hover:bg-[#2d4a8e]">
                <a href="https://www.sihmt.in" target="_blank" rel="noopener noreferrer">
                  Visit SIHMT Portal <ExternalLink className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
            <div className="relative">
              <img 
                src={partnershipImage}
                alt="University partnership MOU signing ceremony" 
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment Form */}
      <section id="enrollment-form" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <Card className="shadow-2xl border-0 overflow-hidden">
            <div className="bg-[#1a2a5e] text-white p-8 text-center">
              <h2 className="font-manrope text-2xl font-bold">Enroll Now</h2>
              <p className="text-blue-100 mt-2">Fill the form below to get started with your healthcare career</p>
            </div>
            <CardContent className="p-8">
              <LeadForm source="course-enrollment" />
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Courses;
